/**
 * SEO helpers for meta tags and structured data.
 */

const JSON_LD_ID = 'activity-json-ld';
const DEFAULT_IMG = 'https://cdn-xcxcustom.weiyoubot.cn/20181109/391d801655b50166d0fc10420c0232c8.png';

const stripHtml = (text = '') => text.replace(/<[^>]+>/g, '').trim();

/**
 * Build a short plain-text description from detail data.
 */
const getDescription = (detail) => {
  if (!detail) return '';

  if (detail.description_brief) {
    return stripHtml(detail.description_brief);
  }

  if (detail.content && Array.isArray(detail.content)) {
    const textItem = detail.content.find((item) => item.type === 'text' && item.value);
    if (textItem) {
      const text = stripHtml(textItem.value);
      return text.length > 200 ? `${text.substring(0, 200)}...` : text;
    }
  }

  if (detail.summary) {
    return stripHtml(detail.summary);
  }

  return stripHtml(detail.title || '');
};

/**
 * Convert a unix timestamp to ISO format.
 */
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toISOString();
};

const getCurrentUrl = () => window.location.href.split('#')[0];

const getOrganizer = (detail) => {
  if (!detail || !detail.sign_name) return undefined;

  const organizer = {
    '@type': detail.auth === 1 ? 'Person' : 'Organization',
    name: detail.sign_name,
  };

  if (detail.owner_unionid) {
    organizer.url = `${window.location.origin}/h/${detail.owner_unionid}`;
  }

  return organizer;
};

const getLocation = (detail) => {
  if (!detail || !detail.address) return undefined;

  return {
    '@type': 'Place',
    name: detail.address,
    address: {
      '@type': 'PostalAddress',
      addressLocality: detail.address,
    },
  };
};

const getOffer = (detail) => {
  if (!detail) return undefined;

  let price = 0;
  if (detail.items_fee && detail.items_fee.fee > 0) {
    price = detail.items_fee.fee / 100;
  } else if (detail.team_items && detail.team_items.team_fee > 0) {
    price = detail.team_items.team_fee / 100;
  } else if (detail.fee > 0) {
    price = detail.fee / 100;
  }

  return {
    '@type': 'Offer',
    price: String(price),
    priceCurrency: 'CNY',
    url: getCurrentUrl(),
  };
};

/**
 * Update or create a meta tag.
 */
const setMetaTag = (nameOrProperty, content, isProperty = false) => {
  const selector = isProperty
    ? `meta[property="${nameOrProperty}"]`
    : `meta[name="${nameOrProperty}"]`;

  let meta = document.querySelector(selector);
  if (!meta) {
    meta = document.createElement('meta');
    if (isProperty) {
      meta.setAttribute('property', nameOrProperty);
    } else {
      meta.setAttribute('name', nameOrProperty);
    }
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content || '');
};

/**
 * Replace the page JSON-LD block with fresh event data.
 */
const updateJsonLd = (detail) => {
  if (!detail || !detail.title) return;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: detail.title,
    description: getDescription(detail),
    url: getCurrentUrl(),
    image: detail.banner ? [detail.banner] : [DEFAULT_IMG],
    organizer: getOrganizer(detail),
    location: getLocation(detail),
    startDate: formatDate(detail.start_time || detail.act_start),
    endDate: formatDate(detail.end_time || detail.act_end),
    offers: getOffer(detail),
  };

  Object.keys(jsonLd).forEach((key) => {
    const value = jsonLd[key];
    if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
      delete jsonLd[key];
    }
  });

  const oldScript = document.getElementById(JSON_LD_ID);
  if (oldScript) oldScript.remove();

  const script = document.createElement('script');
  script.id = JSON_LD_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(script);
};

/**
 * Update all page-level SEO metadata.
 * @param {Object} detail - Activity detail payload.
 */
export function updateAllSeo(detail) {
  if (!detail || !detail.title) return;

  const description = getDescription(detail);

  setMetaTag('description', description);
  setMetaTag('og:title', detail.title, true);
  setMetaTag('og:description', description, true);
  setMetaTag('og:image', detail.banner || DEFAULT_IMG, true);
  setMetaTag('og:url', getCurrentUrl(), true);
  setMetaTag('og:type', 'website', true);
  updateJsonLd(detail);
}

export default {
  updateAllSeo
};
